import { bisector, scaleLinear, ScaleLinear } from 'd3';
import React, { useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import { AreaGraph } from '../../classes/areaGraph';
import { BarGraph } from '../../classes/barGraph';
import { EmptyBarGraph } from '../../classes/emptyBarGraph';
import { Graph } from '../../classes/graph';
import { GraphEvents } from '../../classes/graphEvents';
import { BarVariant, ChartDimensions, IBarPoint, IBarPoints } from '../../interfaces/barGraph';
import useForceUpdate from '../../../core/hooks/useForceUpdate';
import { HdTooltip } from '../../../components';

export interface GraphComponentProps {
  data: IBarPoints;
  graphWidth: number;
  graphHeight: number;
  valuesRange?: number[];
  customChartDimensions?: ChartDimensions;
  isEmpty?: boolean;
  emptyBarCount: number;
  barsSpacing: number;
  barWidth: number;
  barBackdrop?: boolean;
  showArea?: boolean;
  isDarkMode?: boolean;
  barVariant?: BarVariant;
  defaultYAxisDomain?: number[];
  tickCount?: number;
  withAxis?: boolean;
  stackedVariantColors?: string[];
  ToolTipComponent: React.FC<any>;
  YAxisItemContent?: React.FC<any>;
  XAxisItemContent?: React.FC<any>;
  className?: string;
}

export function GraphComponent({
  data,
  graphWidth,
  graphHeight,
  valuesRange = null,
  customChartDimensions = null,
  isEmpty = false,
  emptyBarCount,
  barsSpacing,
  barWidth,
  barBackdrop = false,
  showArea = false,
  isDarkMode = false,
  barVariant = BarVariant.NORMAL,
  defaultYAxisDomain,
  tickCount = 4,
  withAxis = false,
  stackedVariantColors,
  ToolTipComponent,
  YAxisItemContent = null,
  XAxisItemContent = null,
  className = ''
}: GraphComponentProps) {
  const forceUpdate = useForceUpdate();
  const svgRef = useRef(null);
  const xScaleRef = useRef<ScaleLinear<number, number>>();
  const yScaleRef = useRef<ScaleLinear<number, number>>();
  const yAxisTicksRef = useRef<number[]>();
  const actualGraphRendered = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<Graph>(null);
  const barGraphRef = useRef<BarGraph>(null);
  const gEventsRef = useRef<GraphEvents>();
  const lastHoveredPointRef = useRef(null);
  const toolTip = useRef(null);
  const areaGraphRef = useRef<AreaGraph>();
  const [yAxisTicks, setYAxisTicks] = useState([]);

  // const toolTipStyleRef = useRef(null);
  useEffect(() => {
    if (isEmpty) {
      actualGraphRendered.current = false;
      renderEmptyGraph();
      return;
    }
    if (!actualGraphRendered.current) {
      actualGraphRendered.current = true;
      renderGraph();
      return;
    }

    updateGraph();
    handleMouseMove();
    handleMouseLeave();
  }, [data, isEmpty]);

  useEffect(
    () => () => {
      _mouseMoveSub.current.unsubscribe();
      _mouseLeaveSub.current.unsubscribe();
    },
    []
  );

  const renderGraph = () => {
    containerRef.current.innerHTML = '';
    const graph = new Graph(
      containerRef.current,
      graphWidth,
      graphHeight,
      barWidth,
      data,
      valuesRange,
      customChartDimensions
    );
    graphRef.current = graph;
    svgRef.current = graph.drawSvgContainer();

    const yRange = graph.getYRange();
    const chartDimensions = graph.getChartDimensions();

    setScale();

    if (showArea) {
      areaGraphRef.current = new AreaGraph(
        svgRef.current,
        data,
        xScaleRef.current,
        yScaleRef.current,
        yRange[0],
        isDarkMode
      );
      areaGraphRef.current.drawArea();
    }

    barGraphRef.current = new BarGraph(
      svgRef.current,
      data,
      xScaleRef.current,
      yScaleRef.current,
      chartDimensions,
      yRange[0],
      barsSpacing,
      barVariant,
      barBackdrop,
      stackedVariantColors
    );

    barGraphRef.current.drawBarGraph();
    gEventsRef.current = new GraphEvents(svgRef.current, chartDimensions);
    handleMouseMove();
    handleMouseLeave();
  };

  const updateGraph = () => {
    graphRef.current.updateData(graphWidth, graphHeight, data, valuesRange, customChartDimensions);

    const yRange = graphRef.current.getYRange();
    const chartDimensions = graphRef.current.getChartDimensions();

    setScale();

    if (showArea) {
      areaGraphRef.current.updateAreaGraph(
        data,
        xScaleRef.current,
        yScaleRef.current,
        yRange[0],
        isDarkMode
      );
    }

    barGraphRef.current.updateBarGraph(
      data,
      xScaleRef.current,
      yScaleRef.current,
      yRange[0],
      chartDimensions,
      barsSpacing,
      barVariant,
      stackedVariantColors
    );
  };

  const renderEmptyGraph = () => {
    containerRef.current.innerHTML = '';
    const graph = new EmptyBarGraph(
      containerRef.current,
      graphWidth,
      graphHeight,
      emptyBarCount,
      barWidth,
      barsSpacing,
      barBackdrop
    );

    graph.drawBarGraph();
  };

  const setScale = () => {
    const { xScale, yScale } = graphRef.current.getGraphScale(withAxis ? tickCount : null);

    xScaleRef.current = xScale;
    yScaleRef.current = yScale;

    if (!withAxis) {
      return;
    }

    const domain = yScale.domain();
    if (isEmpty || domain[0] === domain[1]) {
      setYAxisTicks(
        scaleLinear().domain(defaultYAxisDomain).nice(tickCount).ticks(tickCount).reverse()
      );
    } else {
      setYAxisTicks(yScale.copy().ticks(tickCount).reverse());
    }

    yAxisTicksRef.current = yAxisTicks.slice(0, yAxisTicks.length - 1);
  };
  const _mouseMoveSub = useRef(Subscription.EMPTY);
  const handleMouseMove = () => {
    _mouseMoveSub.current.unsubscribe();

    _mouseMoveSub.current = gEventsRef.current.onMouseMove().subscribe(mouseCoordinates => {
      const yRange = graphRef.current.getYRange();

      const point = getPointFromCoordinates(xScaleRef.current, mouseCoordinates, data);

      if (point.extra.hideTooltip) {
        lastHoveredPointRef.current = null;
        barGraphRef.current.resetBarsOpacity();
        toolTip.current = { isOpen: false, title: null };
        forceUpdate();
        return;
      }
      if (lastHoveredPointRef.current && point === lastHoveredPointRef.current) {
        return;
      }
      lastHoveredPointRef.current = point;

      barGraphRef.current.fadeBarsOnHover(point);

      const left = `${xScaleRef.current(point.name) + barWidth / 2}px`;
      const top = `${yScaleRef.current(yRange[0]) - 6}px`;
      toolTip.current = { isOpen: true, title: point.extra.tooltipData, style: { left, top } };
      forceUpdate();
    });
  };

  const _mouseLeaveSub = useRef(Subscription.EMPTY);
  const handleMouseLeave = () => {
    _mouseLeaveSub.current.unsubscribe();
    _mouseLeaveSub.current = gEventsRef.current
      .onMouseLeave()
      .pipe()
      .subscribe(() => {
        handleMouseLeave();
        lastHoveredPointRef.current = null;
      });
    barGraphRef.current?.resetBarsOpacity();
  };

  return (
    <div className={`d-flex ${className}`} data-id='latency-graph'>
      {withAxis && (
        <ul className='graph-y-axis' style={{ height: `${graphHeight}px` }}>
          {yAxisTicks.map((item, index) => (
            <li key={index}>
              <YAxisItemContent item={item} />
            </li>
          ))}
        </ul>
      )}

      <div>
        <div
          className='graph-canvas'
          onMouseLeave={() => {
            toolTip.current = { isOpen: false, title: null };
            forceUpdate();
          }}
        >
          <div className='d-flex' ref={containerRef} />

          <HdTooltip
            leaveTouchDelay={0}
            arrow
            disabled={!toolTip.current?.isOpen}
            forceOpen={toolTip.current?.isOpen}
            title={<ToolTipComponent data={toolTip.current?.title} />}
            placement='bottom'
          >
            <div
              style={{
                left: toolTip.current?.style?.left,
                top: toolTip.current?.style?.top,
                position: 'absolute'
              }}
            />
          </HdTooltip>
        </div>

        {withAxis && (
          <ul
            className='graph-x-axis'
            style={{
              marginLeft: `${barsSpacing * -0.5}px`,
              marginRight: `${barsSpacing * -0.5}px`
            }}
          >
            {data.map((item, index) => (
              <li key={index}>
                <XAxisItemContent item={item} index={index} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const getPointFromCoordinates = (xScale, mouseCoordinates, data) => {
  const getXAxisIndex = bisector((d: any) => d.name).left;
  const x0 = xScale.invert(mouseCoordinates[0]);
  const i = getXAxisIndex(data, x0, 1);
  const d0 = data[i - 1];
  const d1 = data[i];
  let d: IBarPoint;

  if (d1) {
    d = x0 - d0.name > d1.name - x0 ? d1 : d0;
  } else {
    d = d0;
  }
  return d;
};
